import React, { FC, useRef, useState } from 'react';
import { IoArrowBack, IoArrowForward } from 'react-icons/io5';

import { Carousel } from '@mantine/carousel';
import { Box, Center, Flex, Modal } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import Autoplay, { AutoplayType } from 'embla-carousel-autoplay';

import { COMPONENT_IMAGE_TYPE_OPTIONS, Component, File } from 'models';
import { ComponentImageHelpers } from 'helpers/ComponentImageHelpers';

import { ComponentIcon } from 'components/component-icons/ComponentIcon';
import { IKImage } from 'components/ik-image/IKImage';
import { useCompanyProfile } from 'hooks/use-company-profile';

import classes from './ComponentDatasheetHeaderImages.module.scss';

const IMAGE_SIZE = 400;
const LABEL_SIZE = 40;

type Props = {
    images: Component['images'];
    imageHeight?: number;
    showType?: boolean;
    onImageClick?: (index: number) => void;
    enableAutoplay?: boolean;
    initialSlide?: number;
    manufacturer?: Component['manufacturer'];
    componentType?: Component['type'];
};

const ComponentDatasheetHeaderImages: FC<Props> = ({
    images: passedImages,
    imageHeight = IMAGE_SIZE,
    showType,
    onImageClick,
    enableAutoplay,
    initialSlide,
    manufacturer,
    componentType,
}) => {
    const autoplay = useRef<AutoplayType>(Autoplay({ delay: 5000 }));

    const carouselHeight = showType ? imageHeight + LABEL_SIZE : imageHeight;
    const images = ComponentImageHelpers.withThumbnailFirst(passedImages);

    if (images.length === 0) {
        return (
            <ComponentDatasheetFallbackImage
                manufacturer={manufacturer}
                componentType={componentType}
                height={imageHeight}
            />
        );
    }

    return (
        <Carousel
            emblaOptions={{
                loop: true,
            }}
            slideGap="xs"
            classNames={classes}
            initialSlide={initialSlide}
            withControls={images.length > 1}
            height={carouselHeight}
            plugins={enableAutoplay ? [autoplay.current as any] : []}
            previousControlIcon={<IoArrowBack />}
            nextControlIcon={<IoArrowForward />}
        >
            {images.map((image: any, index: number) => (
                <DatasheetHeaderImage
                    index={index}
                    image={image}
                    height={imageHeight}
                    showType={showType}
                    key={index}
                    handleClick={onImageClick}
                />
            ))}
        </Carousel>
    );
};

const ComponentDatasheetFallbackImage: FC<{
    manufacturer?: Component['manufacturer'];
    componentType?: Component['type'];
    height: number;
}> = ({ manufacturer, componentType, height }) => {
    const { company } = useCompanyProfile(manufacturer);

    const hasCompanyLogo = company && company.logos?.small;

    if (hasCompanyLogo) {
        return (
            <Center h={height} w="100%">
                <Box w={height * 0.8}>
                    <IKImage
                        fileOrId={company.logos.small as File}
                        width={height * 0.8}
                        radius="xs"
                        alt="Company logo"
                        fit="contain"
                        styles={{
                            root: {
                                overflow: 'hidden',
                                filter: 'grayscale(1) opacity(0.2)',
                            },
                        }}
                    />
                </Box>
            </Center>
        );
    }

    if (componentType) {
        return (
            <Center h={height} w="100%">
                <Box w={height * 0.6} h={height * 0.6} opacity={0.2}>
                    <ComponentIcon resizing type={componentType} />
                </Box>
            </Center>
        );
    }

    return (
        <Center h={height} w="100%" c="dimmed">
            No image available
        </Center>
    );
};

const DatasheetHeaderImage: FC<{
    index: number;
    image: Component['images'][number];
    height: number;
    showType?: boolean;
    handleClick?: (index: number) => void;
}> = ({ index, image, height, showType, handleClick }) => {
    const slideHeight = showType ? height + LABEL_SIZE : height;
    const type = COMPONENT_IMAGE_TYPE_OPTIONS.find((item) => item.value === image.type);

    return (
        <>
            <Carousel.Slide
                // Center the image
                m="auto"
                w="100%"
                mah={slideHeight}
                onClick={handleClick ? () => handleClick(index) : undefined}
                style={{ cursor: handleClick ? 'zoom-in' : 'default' }}
            >
                {image.file && (
                    <IKImage
                        fileOrId={image.file as any}
                        width={height > IMAGE_SIZE ? 740 : 400}
                        radius="xs"
                        m="auto"
                        mah={height}
                        alt={type?.label}
                        fit="contain"
                        styles={{
                            root: {
                                overflow: 'hidden',
                            },
                        }}
                    />
                )}
                {showType && (
                    <Flex align="center" justify="center" h={LABEL_SIZE} ta="center" fz="sm" c="dimmed">
                        {type?.label}
                    </Flex>
                )}
            </Carousel.Slide>
        </>
    );
};

const ComponentDatasheetHeaderImagesWithModal: FC<Props> = (props) => {
    const [fullscreen, handlers] = useDisclosure();
    const [clickedImageIndex, setClickedImageIndex] = useState(0);

    const handleClick = (index: number) => {
        handlers.open();
        setClickedImageIndex(index);
    };

    return (
        <>
            <Modal opened={fullscreen} onClose={handlers.close} withCloseButton={false} size="xl">
                <ComponentDatasheetHeaderImages
                    {...props}
                    imageHeight={600}
                    showType
                    initialSlide={clickedImageIndex}
                />
            </Modal>

            <ComponentDatasheetHeaderImages {...props} onImageClick={handleClick} enableAutoplay />
        </>
    );
};

export { ComponentDatasheetFallbackImage, ComponentDatasheetHeaderImages, ComponentDatasheetHeaderImagesWithModal };
